/**
 * Super-admin — Cross-tenant client management.
 *
 * Visual: agrupa clientes POR EMPRESA. Cada empresa vira um card colapsável
 * com cabeçalho próprio (avatar colorido, nome, plano, contagem). Fica
 * imediatamente claro de qual tenant cada cliente vem — não tem como
 * confundir.
 *
 * Endpoints (all bypass tenant scope on backend):
 *   GET    /super-admin/clients?tenant_id=&q=
 *   POST   /super-admin/clients
 *   PATCH  /super-admin/clients/{id}
 *   DELETE /super-admin/clients/{id}
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  RefreshCw,
  Save,
  X as XIcon,
  Briefcase,
  Building2,
  ChevronDown,
  ChevronRight,
  LayoutList,
  LayoutGrid,
  Users,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { formatUSPhone } from "../lib/format";
import { useConfirm } from "../hooks/useConfirm";
import {
  tenantColor,
  initials,
  usageStatus,
  usageChipClass,
} from "../lib/tenantVisuals";
import { logError } from "../lib/logger";

// (paleta movida para lib/tenantVisuals.js — compartilhada com Collaborators)

export default function SuperAdminClients() {
  const confirm = useConfirm();
  const [tenants, setTenants] = useState([]);
  const [filterTenant, setFilterTenant] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState("grouped"); // "grouped" | "flat"
  const [collapsed, setCollapsed] = useState(() => new Set());

  async function loadTenants() {
    try {
      const { data } = await api.get("/super-admin/tenants");
      setTenants(data || []);
    } catch (e) {
      logError("Failed to load tenants", e);
    }
  }

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filterTenant) params.tenant_id = filterTenant;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/super-admin/clients", { params });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTenants();
  }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTenant]);

  async function doDelete(row) {
    const ok = await confirm({
      title: `Excluir cliente "${row.name}"?`,
      description: `Esta ação é permanente. O cliente será removido da empresa "${row.tenant_name || row.tenant_id}".`,
      destructive: true,
      actionLabel: "Excluir",
      requireText: row.name,
    });
    if (!ok) return;
    try {
      await api.delete(`/super-admin/clients/${row.id}`);
      toast.success(`"${row.name}" excluído`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  // Agrupa por tenant_id (preservando metadata vinda do /super-admin/tenants
  // quando disponível — assim conseguimos exibir plano + slug por grupo).
  const tenantMeta = useMemo(() => {
    const m = new Map();
    for (const t of tenants) m.set(t.id, t);
    return m;
  }, [tenants]);

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const r of rows) {
      const key = r.tenant_id || "(sem-tenant)";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    }
    // Ordena empresas pelo nome do tenant; clientes pelo nome do cliente.
    return Array.from(groups.entries())
      .map(([tid, list]) => ({
        tid,
        clients: [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        meta: tenantMeta.get(tid),
        name:
          tenantMeta.get(tid)?.name ||
          list[0]?.tenant_name ||
          "(sem empresa)",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, tenantMeta]);

  const toggleGroup = (tid) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid);
      else next.add(tid);
      return next;
    });
  };

  return (
    <div className="space-y-5" data-testid="super-admin-clients-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Clientes <span className="text-violet-300/80 font-normal">(todas as empresas)</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            <b>{rows.length}</b> cliente{rows.length !== 1 && "s"} em{" "}
            <b>{grouped.length}</b> empresa{grouped.length !== 1 && "s"} · veja,
            edite, crie e exclua clientes de qualquer tenant.
          </p>
        </div>
        <div className="flex gap-2">
          <ViewToggle view={view} setView={setView} />
          <button
            onClick={() => setCreating(true)}
            data-testid="new-client-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm font-semibold text-white"
          >
            <Plus size={14} /> Novo cliente
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-semibold text-slate-200"
            title="Recarregar"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr,280px] gap-2">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por nome, e-mail ou telefone… (Enter)"
            data-testid="client-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-white/10 bg-white/5 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={filterTenant}
          onChange={(e) => setFilterTenant(e.target.value)}
          data-testid="client-filter-tenant"
          className="px-3 py-2.5 rounded-lg border border-white/10 bg-slate-900 text-sm text-white"
        >
          <option value="">Todas as empresas</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400 bg-white/5 border border-white/10 rounded-xl">
          Carregando…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-slate-400 bg-white/5 border border-white/10 rounded-xl">
          Nenhum cliente encontrado{filterTenant && " nesta empresa"}.
        </div>
      ) : view === "grouped" ? (
        <div className="space-y-3">
          {grouped.map((g) => (
            <TenantGroup
              key={g.tid}
              group={g}
              tenants={tenants}
              collapsed={collapsed.has(g.tid)}
              onToggle={() => toggleGroup(g.tid)}
              onEdit={setEditing}
              onDelete={doDelete}
              onAddInTenant={() => {
                // Pre-select this tenant when creating
                setCreating({ tenant_id: g.tid });
              }}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="divide-y divide-white/5">
            {rows.map((r) => (
              <FlatRow
                key={r.id}
                row={r}
                onEdit={() => setEditing(r)}
                onDelete={() => doDelete(r)}
              />
            ))}
          </div>
        </div>
      )}

      {(creating || editing) && (
        <ClientModal
          row={editing}
          presetTenantId={typeof creating === "object" ? creating?.tenant_id : null}
          tenants={tenants}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function ViewToggle({ view, setView }) {
  return (
    <div className="inline-flex rounded-lg border border-white/10 bg-white/5 p-0.5" data-testid="client-view-toggle">
      <button
        onClick={() => setView("grouped")}
        data-testid="view-grouped"
        title="Agrupar por empresa"
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold transition ${
          view === "grouped"
            ? "bg-violet-600 text-white"
            : "text-slate-300 hover:text-white"
        }`}
      >
        <LayoutGrid size={13} /> Por empresa
      </button>
      <button
        onClick={() => setView("flat")}
        data-testid="view-flat"
        title="Lista plana"
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-semibold transition ${
          view === "flat"
            ? "bg-violet-600 text-white"
            : "text-slate-300 hover:text-white"
        }`}
      >
        <LayoutList size={13} /> Lista
      </button>
    </div>
  );
}

function TenantGroup({ group, collapsed, onToggle, onEdit, onDelete, onAddInTenant }) {
  const c = tenantColor(group.tid);
  const meta = group.meta;
  // Usage vs plan limit — usa client_count do tenants endpoint quando
  // disponível, senão cai para o tamanho do grupo (filtros podem reduzir).
  const currentClients =
    typeof meta?.client_count === "number" ? meta.client_count : group.clients.length;
  const usage = usageStatus(currentClients, meta?.limits?.max_clients);
  return (
    <section
      className="rounded-xl border border-white/10 bg-white/5 overflow-hidden"
      data-testid={`tenant-group-${group.tid}`}
    >
      <header
        className={`relative px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-white/5 transition border-l-4`}
        style={{ borderLeftColor: c.solid }}
        onClick={onToggle}
        data-testid={`tenant-group-header-${group.tid}`}
      >
        <button
          className="text-slate-400 hover:text-white"
          aria-label={collapsed ? "Expandir" : "Recolher"}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
        </button>
        <div
          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c.bg} flex items-center justify-center text-white font-bold text-sm shadow-lg flex-shrink-0`}
        >
          {initials(group.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-white text-base truncate">
              {group.name}
            </h3>
            {meta?.plan_id && (
              <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200 border border-white/10">
                {meta.plan_id}
              </span>
            )}
            {meta?.status && meta.status !== "active" && (
              <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40">
                {meta.status}
              </span>
            )}
            {/* Usage indicator */}
            <span
              className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${usageChipClass(
                usage.level,
              )}`}
              title={`Limite do plano: ${meta?.limits?.max_clients || "ilimitado"}`}
              data-testid={`tenant-group-usage-${group.tid}`}
            >
              <Users size={10} /> {usage.text}
              {usage.level === "danger" && " · LIMITE!"}
              {usage.level === "warn" && " · perto do limite"}
            </span>
          </div>
          <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
            <Building2 size={11} />
            <span className="truncate">{meta?.slug || group.tid}</span>
            <span className="text-slate-600">·</span>
            <span className="flex items-center gap-1">
              <Users size={11} /> {group.clients.length} cliente
              {group.clients.length !== 1 && "s"}
              {group.clients.length !== currentClients && (
                <span className="text-slate-500"> (filtrados de {currentClients})</span>
              )}
            </span>
          </div>
          {/* Progress bar (visível só quando há limite definido) */}
          {usage.level !== "unlimited" && (
            <div className="mt-1.5 h-1 w-full max-w-[240px] rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${usage.percent}%`,
                  background:
                    usage.level === "danger"
                      ? "#ef4444"
                      : usage.level === "warn"
                        ? "#f59e0b"
                        : "#10b981",
                }}
              />
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAddInTenant();
          }}
          data-testid={`tenant-group-add-${group.tid}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-white/10 bg-white/5 hover:bg-violet-600 hover:border-violet-500 transition text-slate-200 hover:text-white"
          title="Adicionar cliente nesta empresa"
        >
          <Plus size={11} /> Cliente
        </button>
      </header>

      {!collapsed && (
        <div className="divide-y divide-white/5 border-t border-white/5">
          {group.clients.map((r) => (
            <ClientRow
              key={r.id}
              row={r}
              accent={c.solid}
              onEdit={() => onEdit(r)}
              onDelete={() => onDelete(r)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ClientRow({ row, accent, onEdit, onDelete }) {
  return (
    <div
      className="px-4 py-3 grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-3 items-center hover:bg-white/5"
      data-testid={`client-row-${row.id}`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div
          className="w-1 self-stretch rounded-full flex-shrink-0"
          style={{ background: accent }}
          aria-hidden
        />
        <div
          className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 text-slate-200 flex items-center justify-center flex-shrink-0 text-xs font-bold"
        >
          {initials(row.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-white truncate flex items-center gap-1.5">
            <Briefcase size={12} className="text-slate-500 flex-shrink-0" />
            {row.name}
            {row.frequency && (
              <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-200 border border-violet-500/30">
                {row.frequency}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 truncate flex items-center gap-1.5 mt-0.5">
            <MapPin size={11} className="flex-shrink-0" />
            <span className="truncate">{row.address || "(sem endereço)"}</span>
          </div>
          <div className="text-[11px] text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
            {row.phone && (
              <span className="flex items-center gap-1">
                <Phone size={10} /> {row.phone}
              </span>
            )}
            {row.email && (
              <span className="flex items-center gap-1 truncate">
                <Mail size={10} /> {row.email}
              </span>
            )}
            {row.value > 0 && (
              <span className="flex items-center gap-1 text-emerald-300 font-semibold">
                ${Number(row.value).toFixed(2)}/mês
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-1.5">
        <button
          onClick={onEdit}
          data-testid={`client-edit-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"
        >
          <Pencil size={12} /> Editar
        </button>
        <button
          onClick={onDelete}
          data-testid={`client-delete-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-500/40 bg-red-500/15 text-red-200 hover:bg-red-500/25"
        >
          <Trash2 size={12} /> Excluir
        </button>
      </div>
    </div>
  );
}

/** Linha usada no modo "Lista plana" — destaca o tenant com pílula colorida. */
function FlatRow({ row, onEdit, onDelete }) {
  const c = tenantColor(row.tenant_id);
  return (
    <div
      className="px-4 py-3 grid grid-cols-1 sm:grid-cols-[260px,1fr,auto] gap-3 items-center hover:bg-white/5"
      data-testid={`client-row-${row.id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={`w-7 h-7 rounded-lg bg-gradient-to-br ${c.bg} flex items-center justify-center text-white font-bold text-[11px] flex-shrink-0 shadow`}
          title={row.tenant_name || row.tenant_id}
        >
          {initials(row.tenant_name || "")}
        </div>
        <div
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${c.chip} text-xs font-semibold truncate`}
        >
          <Building2 size={11} className="flex-shrink-0" />
          <span className="truncate">{row.tenant_name || row.tenant_id}</span>
        </div>
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-white truncate">{row.name}</div>
        <div className="text-xs text-slate-400 truncate">{row.address}</div>
      </div>
      <div className="flex justify-end gap-1.5">
        <button
          onClick={onEdit}
          data-testid={`client-edit-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"
        >
          <Pencil size={12} /> Editar
        </button>
        <button
          onClick={onDelete}
          data-testid={`client-delete-${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-red-500/40 bg-red-500/15 text-red-200 hover:bg-red-500/25"
        >
          <Trash2 size={12} /> Excluir
        </button>
      </div>
    </div>
  );
}

function ClientModal({ row, presetTenantId, tenants, onClose, onSaved }) {
  const isNew = !row;
  const [form, setForm] = useState({
    name: row?.name || "",
    address: row?.address || "",
    phone: row?.phone || "",
    email: row?.email || "",
    value: row?.value ?? 0,
    payment_due_day: row?.payment_due_day ?? "",
    observations: row?.observations || "",
    tenant_id:
      row?.tenant_id || presetTenantId || tenants[0]?.id || "",
  });
  const [saving, setSaving] = useState(false);
  const tenantInfo = tenants.find((t) => t.id === form.tenant_id);
  const c = tenantColor(form.tenant_id);

  async function save() {
    if (!form.name.trim() || !form.address.trim()) {
      toast.error("Nome e endereço são obrigatórios");
      return;
    }
    if (isNew && !form.tenant_id) {
      toast.error("Selecione uma empresa");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        value: Number(form.value) || 0,
        payment_due_day:
          form.payment_due_day === "" ? null : Number(form.payment_due_day),
      };
      if (isNew) {
        await api.post("/super-admin/clients", body);
        toast.success(`Cliente criado em ${tenantInfo?.name || "empresa"}`);
      } else {
        await api.patch(`/super-admin/clients/${row.id}`, body);
        toast.success("Cliente atualizado");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full text-white max-h-[90vh] overflow-y-auto"
        data-testid="client-modal"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-wider text-violet-300 font-semibold">
              {isNew ? "Novo cliente" : "Editar cliente"}
            </div>
            <h2 className="text-lg font-bold text-white">
              {form.name || "(sem nome)"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"
          >
            <XIcon size={18} />
          </button>
        </div>

        {/* Banner Empresa */}
        <div
          className="mx-5 mt-5 rounded-xl border p-3 flex items-center gap-3"
          style={{ borderColor: c.solid + "55", background: c.solid + "12" }}
        >
          <div
            className={`w-10 h-10 rounded-lg bg-gradient-to-br ${c.bg} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
          >
            {initials(tenantInfo?.name || "?")}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              {isNew ? "Será adicionado em" : "Cliente pertence a"}
            </div>
            <select
              value={form.tenant_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, tenant_id: e.target.value }))
              }
              data-testid="client-modal-tenant"
              className="w-full bg-transparent text-white font-bold outline-none border-b border-white/10 focus:border-violet-500 transition"
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id} className="bg-slate-900">
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <Field label="Nome">
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="client-modal-name"
              className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Endereço">
            <input
              value={form.address}
              onChange={(e) =>
                setForm((f) => ({ ...f, address: e.target.value }))
              }
              data-testid="client-modal-address"
              className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefone">
              <input
                value={form.phone}
                type="tel"
                inputMode="tel"
                maxLength={14}
                placeholder="(555) 123-4567"
                onChange={(e) =>
                  setForm((f) => ({ ...f, phone: formatUSPhone(e.target.value) }))
                }
                className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="E-mail">
              <input
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, email: e.target.value }))
                }
                className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Valor mensal (R$)">
              <input
                type="number"
                step="0.01"
                value={form.value}
                onChange={(e) =>
                  setForm((f) => ({ ...f, value: e.target.value }))
                }
                className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Dia de vencimento">
              <input
                type="number"
                min="1"
                max="31"
                value={form.payment_due_day}
                onChange={(e) =>
                  setForm((f) => ({ ...f, payment_due_day: e.target.value }))
                }
                placeholder="Ex.: 10"
                className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <Field label="Observações">
            <textarea
              value={form.observations}
              onChange={(e) =>
                setForm((f) => ({ ...f, observations: e.target.value }))
              }
              rows={2}
              className="w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10 sticky bottom-0 bg-slate-900">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="client-modal-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm font-semibold disabled:opacity-50"
          >
            <Save size={14} /> {isNew ? "Criar" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-300 mb-1 block">
        {label}
      </span>
      {children}
    </label>
  );
}
